/* What a CLI asks for at /cli-login, and whether we will honour it. Both the AI21 Gateway
 * and hopt CLIs use this route and this request shape; only the copy differs.
 *
 * `redirect_uri` decides where a Firebase refresh token gets posted, which makes it the
 * most security-sensitive input this app takes from a URL — that token grants the user's
 * whole AI21 identity. It is therefore parsed with `new URL()` and checked field by field,
 * never string-matched: every classic bypass parses cleanly and defeats a prefix test.
 *
 *   https://127.0.0.1.evil.com/callback   — a hostname that merely *starts* with the
 *                                           loopback address
 *   http://evil.com@127.0.0.1/callback    — userinfo, so a careless reader sees evil.com
 *                                           while the browser connects to loopback
 *   http://127.0.0.1#@evil.com/callback   — a fragment that makes the tail look like a host
 *   http://localhost:1234/callback        — a name, and a name can be repointed
 *   http://[::1]:1234/callback            — loopback, but not the one the CLI binds
 *
 * `exp` bounds how long any of this is believed. The CLI's listener does not outlive its
 * deadline but this tab does, and once the CLI exits the OS may hand that port to anything
 * — so a genuine URL left open in a tab would otherwise post a real credential to whoever
 * owns the port by then. The verification code cannot cover that case: it is rendered from
 * this URL, so it still matches what the dead run printed.
 *
 * `state` and `code` are validated by shape too. `code` is rendered on the consent screen
 * as the thing the user is told to trust; React escapes it, so this is not about XSS, but
 * an attacker-chosen "code" could display misleading text exactly where the user is
 * looking for reassurance. Only the CLI's own format is accepted.
 */

export interface CliLoginRequest {
  /** Where to post the credential. Always an http://127.0.0.1:<port>/callback URL. */
  redirectUri: string;
  /** The CLI's nonce, echoed back so it can tell its own callback from a stale tab's. */
  state: string;
  /** The short code the CLI printed, shown here so the user can compare the two. */
  code: string;
  /** When the CLI's listener gives up, in milliseconds since the epoch. */
  expiresAt: number;
}

export type CliLoginRefusal =
  /** One of the three parameters is missing — this is not a CLI login request. */
  | "incomplete"
  /** `redirect_uri` is not somewhere we will ever post a token. */
  | "untrusted-callback"
  /** `state` or `code` is not the shape the CLI produces. */
  | "malformed"
  /** The CLI that asked for this has already stopped listening. */
  | "expired";

export type CliLoginRequestResult =
  | { ok: true; request: CliLoginRequest }
  | { ok: false; refusal: CliLoginRefusal };

const CALLBACK_PATH = "/callback";
const LOOPBACK_HOST = "127.0.0.1";
/* The CLI binds with port 0, so the OS always assigns an unprivileged port. A request
 * naming a privileged one did not come from it. */
const LOWEST_UNPRIVILEGED_PORT = 1024;
const HIGHEST_PORT = 65535;

/* `secrets.token_urlsafe(32)` — 43 url-safe base64 characters. Bounded on both sides
 * rather than exact, so the CLI can lengthen it without a lockstep webapp release. */
const STATE_RE = /^[A-Za-z0-9_-]{32,128}$/;
/* The CLI's verification code: two groups of four, from an alphabet with the
 * easily-confused characters left out. */
const CODE_RE = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;

/* The furthest ahead an `exp` is believed. The CLI's own deadline is three minutes; this
 * ceiling is what stops `exp` from becoming a free knob — without it a hand-written URL
 * could stay armed for years, which is the situation the parameter exists to end. */
const MAX_LIFETIME_MS = 10 * 60 * 1000;

/** Whether this URL is one we are willing to post a refresh token to. */
export function isTrustedCallback(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }

  // The CLI's listener is plain HTTP on loopback, which browsers treat as a secure
  // context. Anything else is not it.
  if (url.protocol !== "http:") return false;

  // The literal address, not a name: a name can be pointed anywhere, and `localhost`
  // resolves through the host's own resolver.
  if (url.hostname !== LOOPBACK_HOST) return false;

  if (url.pathname !== CALLBACK_PATH) return false;

  // Userinfo, a query or a fragment all mean this URL was constructed to read as
  // something other than what the browser will do with it.
  if (url.username !== "" || url.password !== "") return false;

  if (url.search !== "" || url.hash !== "") return false;

  // `url.port` is "" when absent, and Number("") is 0 — so a URL with no port is refused
  // by the range check rather than silently accepted as port 80.
  const port = Number(url.port);
  return (
    Number.isInteger(port) &&
    port >= LOWEST_UNPRIVILEGED_PORT &&
    port <= HIGHEST_PORT
  );
}

/** Read a CLI login request off the query string, refusing anything we do not recognise. */
/** Whether an `exp` (unix seconds) is one we will still act on. */
export function isFresh(exp: number, now: number = Date.now()): boolean {
  const expiresAt = exp * 1000;

  return expiresAt > now && expiresAt - now <= MAX_LIFETIME_MS;
}

export function parseCliLoginRequest(
  search: string,
  now: number = Date.now(),
): CliLoginRequestResult {
  const params = new URLSearchParams(search);
  const redirectUri = params.get("redirect_uri");
  const state = params.get("state");
  const code = params.get("code");
  const exp = params.get("exp");

  if (!redirectUri || !state || !code || !exp)
    return { ok: false, refusal: "incomplete" };

  if (!isTrustedCallback(redirectUri))
    return { ok: false, refusal: "untrusted-callback" };

  if (!STATE_RE.test(state) || !CODE_RE.test(code))
    return { ok: false, refusal: "malformed" };

  // Digits only: Number("") is 0 and Number(" 12 ") is 12, either of which would read a
  // malformed value as a timestamp instead of refusing it.
  if (!/^\d{1,15}$/.test(exp)) return { ok: false, refusal: "malformed" };

  // Checked here, on the render path, so an expired request never reaches the point of
  // having an Approve button — and again on the click, because a tab can sit open.
  if (!isFresh(Number(exp), now)) return { ok: false, refusal: "expired" };

  return {
    ok: true,
    request: { redirectUri, state, code, expiresAt: Number(exp) * 1000 },
  };
}
